import { useState } from "react";
import { KitButton } from "@/app/routes/dashboard/components/kit/Button";
import { UserMark } from "@/app/routes/dashboard/components/UserMark";
import { useAuthStore } from "@/stores/authStore";
import { useWorkshopMembers } from "../api/useWorkshopMembers";
import { usePermissions } from "@/hooks/usePermissions";
import ProjectAddPeople from "./ProjectAddPeople";
import WorkshopDialog from "./WorkshopDialog";

type Props = {
  projectId: string;
  projectName: string;
  /** The viewer created the project: only they add and remove people. */
  isOwner: boolean;
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
};

/** "Rana", "Rana and Omar", "Rana, Omar and Anthony". */
function namesList(names: string[]): string {
  if (names.length <= 1) return names[0] ?? "";
  return `${names.slice(0, -1).join(", ")} and ${names[names.length - 1]}`;
}

/** "Added Sep 24". */
function addedOn(iso: string): string {
  return `Added ${new Date(iso).toLocaleDateString("en-US", { month: "short", day: "numeric" })}`;
}

function PersonRow({
  name,
  detail,
  action,
}: {
  name: string;
  detail?: string | null;
  action?: React.ReactNode;
}) {
  return (
    <div className="ws-person">
      <UserMark name={name} size={32} />
      <div className="min-w-0 flex-1">
        <div className="ws-person-name">{name}</div>
        {detail ? <div className="ws-person-detail">{detail}</div> : null}
      </div>
      {action}
    </div>
  );
}

/**
 * Share this project (07 Workshop, Share a project: nobody else yet, adding
 * people, Stop collaborating?). The owner adds people from the organization
 * (Owners and Contributors, and the brand's own guests) and takes them out
 * again; everyone else sees who is in. Removing someone keeps what they made in
 * the project; they lose access to it and its chats until added back. Stop
 * sharing asks first, naming everyone who loses access.
 */
export default function ProjectMembersModal({
  projectId,
  projectName,
  isOwner,
  isOpen,
  onOpenChange,
}: Props) {
  // A guest can't list the organization's people, so a guest-owned project is
  // shared by someone on the team instead.
  const { isGuest } = usePermissions();
  const { owner, members, isLoading, removeMember, removeAll } = useWorkshopMembers(
    isOpen ? projectId : null,
  );
  const viewerId = useAuthStore((s) => s.user?.id ?? null);
  const [confirmStop, setConfirmStop] = useState(false);
  /** The member whose removal is waiting for a second click. */
  const [pendingRemove, setPendingRemove] = useState<string | null>(null);

  const close = () => {
    setPendingRemove(null);
    onOpenChange(false);
  };
  const nameOf = (m: { name: string | null; email: string | null }) => m.name ?? m.email ?? "A teammate";
  const others = members.filter((m) => m.userId !== viewerId);
  const count = (owner ? 1 : 0) + members.length;

  return (
    <>
      <WorkshopDialog
        isOpen={isOpen && !confirmStop}
        onOpenChange={(next) => (next ? onOpenChange(true) : close())}
        title={isOwner ? "Share this project" : "People in this project"}
        description={
          isOwner
            ? members.length > 0
              ? "People you add can work in it: its chats, outputs, files and agents. Each chat stays private until whoever started it shares it."
              : "People you add can work in it: its chats, outputs, files and agents."
            : `${projectName} is shared with these people. Each chat stays private until whoever started it shares it.`
        }
        actions={
          <>
            {isOwner && members.length > 0 ? (
              <KitButton
                type="button"
                variant="secondary"
                className="ws-dialog-btn ws-share-stop-btn"
                onClick={() => setConfirmStop(true)}
              >
                Stop sharing
              </KitButton>
            ) : null}
            <KitButton type="button" className="ws-dialog-btn" onClick={close}>
              Done
            </KitButton>
          </>
        }
        className="ws-share"
      >
        <section className="ws-share-group" aria-label="People in it">
          <h3 className="ws-share-head">
            People in it
            {isLoading ? null : <span className="ws-share-count">{count}</span>}
          </h3>
          {owner ? (
            <PersonRow
              name={owner.name ?? "A teammate"}
              detail={owner.userId === viewerId ? "You" : null}
              action={<span className="ws-person-role">Owner</span>}
            />
          ) : null}
          {isLoading ? (
            <div className="flex flex-col gap-2 py-1.5" aria-hidden>
              {[0, 1].map((i) => (
                <div key={i} className="flex items-center gap-3 px-2">
                  <span className="size-8 shrink-0 animate-pulse rounded-full bg-co-hover" />
                  <span className="h-3 w-32 animate-pulse rounded-full bg-co-hover" />
                </div>
              ))}
            </div>
          ) : (
            members.map((m) => (
              <PersonRow
                key={m.userId}
                name={nameOf(m)}
                detail={
                  pendingRemove === m.userId
                    ? "Loses access to the project and its chats."
                    : m.userId === viewerId
                      ? "You"
                      : addedOn(m.addedAt)
                }
                action={
                  !isOwner ? null : pendingRemove === m.userId ? (
                    <span className="flex shrink-0 items-center gap-1">
                      <KitButton type="button" variant="ghost" size="sm" onClick={() => setPendingRemove(null)}>
                        Keep
                      </KitButton>
                      <KitButton
                        type="button"
                        variant="ghost"
                        size="sm"
                        className="ws-share-danger"
                        onClick={() => {
                          setPendingRemove(null);
                          removeMember.mutate(m.userId);
                        }}
                      >
                        Remove
                      </KitButton>
                    </span>
                  ) : (
                    <KitButton
                      type="button"
                      variant="ghost"
                      size="sm"
                      aria-label={`Remove ${nameOf(m)} from the project`}
                      onClick={() => setPendingRemove(m.userId)}
                    >
                      Remove
                    </KitButton>
                  )
                }
              />
            ))
          )}
        </section>

        {isOwner && isGuest ? (
          <p className="ws-share-note">
            To bring someone into this project, ask your contact on the team to add them.
          </p>
        ) : isOwner ? (
          <ProjectAddPeople projectId={projectId} isOpen={isOpen} heading="Add people" headingId="ws-share-add" />
        ) : null}
      </WorkshopDialog>

      <WorkshopDialog
        isOpen={isOpen && confirmStop}
        onOpenChange={(next) => {
          if (!next) setConfirmStop(false);
        }}
        title="Stop collaborating?"
        description="Everyone else loses access. What they made stays in the project."
        consequence={`${namesList((others.length ? others : members).map(nameOf))} lose${
          (others.length ? others : members).length === 1 ? "s" : ""
        } access`}
        cancelLabel="Keep sharing"
        confirmLabel="Stop collaborating"
        confirmTone="danger"
        onConfirm={() => {
          setConfirmStop(false);
          removeAll.mutate();
        }}
      />
    </>
  );
}
